import { Shield, Truck, RotateCcw, Headphones, Star, Lock, CreditCard, Award } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export interface TrustIndicatorItem {
  icon: React.ElementType;
  text: string;
  subtext?: string;
  color?: string;
  badge?: string;
}

interface TrustIndicatorsProps {
  variant?: 'compact' | 'detailed' | 'checkout' | 'sidebar' | 'inline';
  className?: string;
  showReviews?: boolean;
  showSecurityBadges?: boolean;
  customIndicators?: TrustIndicatorItem[];
}

const TrustIndicators = ({
  variant = 'compact',
  className,
  showReviews = true,
  showSecurityBadges = true,
  customIndicators
}: TrustIndicatorsProps) => {
  
  const defaultIndicators: TrustIndicatorItem[] = [
    {
      icon: Truck,
      text: "Free Shipping w/$150 Purchase",
      color: "text-primary",
      badge: "Popular"
    },
    {
      icon: RotateCcw,
      text: "30-Day Returns",
      subtext: "Hassle-free returns",
      color: "text-info"
    },
    {
      icon: Headphones,
      text: "24/7 Support",
      subtext: "Expert styling help",
      color: "text-accent"
    },
    {
      icon: Shield,
      text: "Secure Checkout",
      subtext: "SSL encrypted",
      color: "text-primary"
    }
  ];

  const securityBadges = [
    { icon: Lock, text: "SSL Secured", color: "text-primary" },
    { icon: CreditCard, text: "Safe Payments", color: "text-info" },
    { icon: Award, text: "Verified Store", color: "text-accent" }
  ];

  const reviewsData = {
    rating: 4.8,
    count: 2847,
    recentReview: "Amazing quality and fast shipping!"
  };

  const indicators = customIndicators || defaultIndicators;

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-3 h-3 ${
          i < Math.floor(rating) ? 'fill-accent text-accent' : 
          i < rating ? 'fill-accent/50 text-accent' : 'text-muted-foreground'
        }`}
      />
    ));
  };

  // Compact variant - for product cards, quick view
  if (variant === 'compact') {
    return (
      <div className={cn("grid grid-cols-2 gap-2 text-xs", className)}>
        {indicators.slice(0, 4).map((indicator, index) => {
          const Icon = indicator.icon;
          return (
            <div key={index} className="flex items-center gap-1.5 text-muted-foreground">
              <Icon className={cn("w-3 h-3", indicator.color)} />
              <span className="truncate">{indicator.text}</span>
            </div>
          );
        })}
      </div>
    );
  }

  // Inline variant - single row of indicators
  if (variant === 'inline') {
    return (
      <div className={cn("flex items-center justify-start gap-2 text-xs tracking-tight", className)}>
        {indicators.slice(0, 4).map((indicator, index) => {
          const Icon = indicator.icon;
          return (
            <div key={index} className="flex items-center gap-1 text-muted-foreground">
              <Icon className={cn("w-3 h-3 flex-shrink-0", indicator.color)} />
              <span className="truncate text-xs">{indicator.text}</span>
            </div>
          );
        })}
      </div>
    );
  }

  // Detailed variant - for product pages
  if (variant === 'detailed') {
    return (
      <div className={cn("space-y-4", className)}>
        {/* Trust Indicators */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {indicators.map((indicator, index) => {
            const Icon = indicator.icon;
            return (
              <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
                <div className={cn("p-2 rounded-full bg-background border", indicator.color)}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{indicator.text}</span>
                    {indicator.badge && (
                      <Badge variant="secondary" className="text-xs px-1.5 py-0.5">
                        {indicator.badge}
                      </Badge>
                    )}
                  </div>
                  {indicator.subtext && (
                    <p className="text-xs text-muted-foreground mt-0.5">{indicator.subtext}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Reviews Section */}
        {showReviews && (
          <Card className="border-dashed border-accent/30">
            <CardContent className="pt-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex">{renderStars(reviewsData.rating)}</div>
                    <span className="font-semibold text-accent">{reviewsData.rating}</span>
                    <span className="text-sm text-muted-foreground">
                      ({reviewsData.count.toLocaleString()} reviews)
                    </span>
                  </div>
                  <p className="text-sm italic text-muted-foreground">"{reviewsData.recentReview}"</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Security Badges */}
        {showSecurityBadges && (
          <div className="flex items-center justify-center gap-4 py-3 border-t border-border/50">
            {securityBadges.map((badge, index) => {
              const Icon = badge.icon;
              return (
                <div key={index} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Icon className={cn("w-3 h-3", badge.color)} />
                  <span>{badge.text}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Checkout variant - for checkout page
  if (variant === 'checkout') {
    return (
      <Card className={cn("border-primary/20 bg-primary/10", className)}>
        <CardContent className="pt-4">
          <div className="space-y-3">
            <div className="flex items-center justify-center gap-2 text-primary">
              <Shield className="w-5 h-5" />
              <span className="font-semibold">Your Order is Protected</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div className="text-center">
                <Lock className="w-4 h-4 mx-auto mb-1 text-primary" />
                <p className="font-medium">SSL Encrypted</p>
                <p className="text-xs text-muted-foreground">Secure checkout</p>
              </div>
              <div className="text-center">
                <CreditCard className="w-4 h-4 mx-auto mb-1 text-info" />
                <p className="font-medium">Safe Payments</p>
                <p className="text-xs text-muted-foreground">Protected transactions</p>
              </div>
              <div className="text-center">
                <RotateCcw className="w-4 h-4 mx-auto mb-1 text-accent" />
                <p className="font-medium">Money Back</p>
                <p className="text-xs text-muted-foreground">30-day guarantee</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Sidebar variant - for cart sidebar
  if (variant === 'sidebar') {
    return (
      <div className={cn("space-y-2", className)}>
        <div className="text-xs font-medium text-muted-foreground mb-2">
          ✨ Your Benefits
        </div>
        {indicators.slice(0, 3).map((indicator, index) => {
          const Icon = indicator.icon;
          return (
            <div key={index} className="flex items-center gap-2 text-xs">
              <Icon className={cn("w-3 h-3", indicator.color)} />
              <span className="text-muted-foreground">{indicator.text}</span>
              {indicator.subtext && (
                <span className="text-muted-foreground/70">- {indicator.subtext}</span>
              )}
            </div>
          );
        })}
        
        {showReviews && (
          <div className="flex items-center gap-1 pt-1 text-xs text-muted-foreground">
            <div className="flex">{renderStars(reviewsData.rating)}</div>
            <span>{reviewsData.rating} ({reviewsData.count.toLocaleString()})</span>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default TrustIndicators;